import React, { useEffect, useState } from 'react';
import type { GetRowKey } from 'rc-table/lib/interface';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { Dropdown, Checkbox, Tooltip, Popover } from 'antd';
import { More, EmptyDefaultColor } from 'patternx-svg';
import styles from './card-list.module.less';
import classNames from 'classnames';
import { isEqual } from 'lodash';
import { WarmingCircle } from 'patternx-svg';

declare type OverlayFunc = () => React.ReactElement;

interface IDataSourceItem {
  extra: React.ReactNode;
  [key: string]: any;
}

interface CardListProps<T> {
  rowKey: string | GetRowKey<T>;
  rowSelection: any;
  dataSource: readonly any[];
  titleKey?: string;
  choiceMode?: boolean;
  subTitle?: (item: T) => React.ReactNode;
  imageUrl?: (item: T) => string;
  defaultIcon?: (item?: T) => React.ReactNode;
  onClick?: (item: T) => void;
  renderMenu?: (item: T) => React.ReactElement | OverlayFunc;
  renderTitle?: (item: T) => React.ReactElement | OverlayFunc;
  hasBrow?: boolean;
  itemBoxStyle?: React.CSSProperties | undefined;
  itemStyle?: React.CSSProperties | undefined;
  disabledKeys?: React.Key[];
}

const CardList: React.FC<CardListProps<IDataSourceItem>> = React.memo((props) => {
  const {
    rowKey,
    titleKey,
    imageUrl,
    defaultIcon = () => <EmptyDefaultColor />,
    subTitle,
    rowSelection,
    dataSource,
    onClick,
    renderMenu,
    renderTitle,
    choiceMode = false,
    disabledKeys = [],
  } = props;
  const { onChange, selectedRowKeys } = rowSelection || {};

  const [open, setOpen] = useState<string[]>([]);
  const [keys, setKeys] = useState<string[]>([]);

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleChecked = (key: string) => {
    let newKeys = keys;
    if (choiceMode) {
      newKeys = isEqual(keys, [key]) ? [] : [key];
    } else {
      if (keys.includes(key)) {
        newKeys = newKeys.filter((item: any) => item !== key);
      } else {
        newKeys = [...keys, key];
      }
    }
    setKeys(newKeys);
    onChange?.(
      newKeys,
      dataSource.filter((item) => newKeys.includes(item?.[rowKey as string])),
    );
  };

  const selectAll = (e: CheckboxChangeEvent) => {
    if (e.target.checked) {
      const newKeys = dataSource.map((item) => item?.[rowKey as string]);
      setKeys(newKeys);
      onChange?.(newKeys, dataSource);
    } else {
      setKeys([]);
      onChange?.([], []);
    }
  };

  const handleVisible = (visible: boolean, id: string) => {
    if (visible) {
      setOpen([...open, id]);
    } else {
      setOpen(open.filter((item) => item !== id));
    }
  };

  useEffect(() => {
    if (!selectedRowKeys) {
      return;
    }
    setKeys(selectedRowKeys);
  }, [selectedRowKeys]);

  const getAbsentBizInfo = (item: any) => {
    const temArr = item.absentBizInfo.split(',');
    const temArr2 = temArr.map((it: any) => {
      return '【' + it + '】';
    });
    const res = temArr2.join(',');
    console.log('getAbsentBizInfo', item, temArr, temArr2, res);
    return res;
  };

  return (
    <>
      {rowSelection && (
        <div className={styles.label}>
          {!choiceMode && (
            <>
              <Checkbox
                onChange={selectAll}
                indeterminate={keys.length > 0 && keys.length < dataSource.length}
                checked={keys.length > 0 && keys.length === dataSource.length}
              >
                全选
              </Checkbox>
              {keys.length > 0 && (
                <span className={styles.notify}>
                  已选择
                  <span className="text-primary" style={{ margin: '0 2px' }}>
                    {keys.length}
                  </span>
                  项
                </span>
              )}
            </>
          )}
        </div>
      )}
      <div className={styles.cardBox}>
        {dataSource.map((item) => {
          const checkedProps = selectedRowKeys
            ? { checked: selectedRowKeys.includes(item.id) }
            : {};
          const hasImage = imageUrl && imageUrl(item);
          return (
            <div key={item?.[rowKey as string]} className={styles.cardBoxContent}>
              {props.hasBrow && <div className={styles.cardbrow} />}
              <div
                key={item?.[rowKey as string]}
                className={classNames(styles.cardItem, {
                  [styles.active]: keys.includes(item?.[rowKey as string]),
                  [styles.disabled]: disabledKeys.includes(item?.[rowKey as string]),
                })}
                style={props.itemStyle}
                onClick={() => {
                  if (choiceMode && !disabledKeys.includes(item?.[rowKey as string])) {
                    handleChecked(item?.[rowKey as string]);
                  }
                  onClick?.(item);
                }}
              >
                {item.bizInfoComplete === false && item.transferStatus === 'transfered' && (
                  <div
                    className={styles.businessTip}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Popover
                      title="业务信息缺失"
                      content={
                        <div style={{ width: '220px' }}>
                          缺失{getAbsentBizInfo(item)}字段，请补充后更新构件
                        </div>
                      }
                      placement="bottomLeft"
                      // className={styles.laberMention}
                      color="#fff"
                      overlayClassName={styles.laberMention}
                    >
                      <WarmingCircle
                        color="rgb(175,178,189)"
                        onClick={(e) => {
                          e.stopPropagation(); // 点击图标时，不进入构件详情页面
                        }}
                      />
                    </Popover>
                  </div>
                )}
                {rowSelection && (
                  <div className={styles.cardCheckbox} onClick={stopPropagation}>
                    <Checkbox
                      {...checkedProps}
                      checked={keys.includes(item?.[rowKey as string])}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleChecked(item?.[rowKey as string]);
                      }}
                    />
                  </div>
                )}
                <div
                  className={classNames(styles.itemImgBox, {
                    [styles.isImageBox]: hasImage,
                  })}
                  style={hasImage ? undefined : props.itemBoxStyle}
                >
                  {hasImage ? (
                    <img className={styles.hasImg} src={imageUrl(item)} alt="" />
                  ) : (
                    <div className={styles.defaultIcon}>{defaultIcon(item)}</div>
                  )}
                </div>
                <div className={styles.itemInfo}>
                  <div className={styles.titleWrap}>
                    {renderTitle ? (
                      renderTitle(item)
                    ) : (
                      <Tooltip
                        title={item?.[titleKey || '']}
                        arrowPointAtCenter={true}
                        getPopupContainer={(triggerNode) => triggerNode}
                        overlayStyle={{
                          wordBreak: 'break-all',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        <div
                          className={classNames(styles.title, 'ellipsis_txt')}
                          title={item?.[titleKey || '']}
                        >
                          {item?.[titleKey || '']}
                        </div>
                      </Tooltip>
                    )}

                    {renderMenu && (
                      <div
                        className={classNames(styles.menus, {
                          [styles.active]: open.includes(item?.[titleKey || '']),
                        })}
                        onClick={stopPropagation}
                      >
                        <Dropdown
                          className="header-menu-dropdown"
                          trigger={['click']}
                          overlay={
                            <div onClick={() => handleVisible(false, item?.[titleKey || ''])}>
                              {renderMenu(item)}
                            </div>
                          }
                          overlayClassName="ptx-menu-box"
                          onVisibleChange={(visible) =>
                            handleVisible(visible, item?.[titleKey || ''])
                          }
                        >
                          <div className={styles.icon}>
                            <More />
                          </div>
                        </Dropdown>
                      </div>
                    )}
                  </div>
                  {subTitle?.(item)}
                </div>
                {item.extra}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
});

export default CardList;
